
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Consultation = Database['public']['Tables']['consultations']['Row'];
type ConsultationInsert = Database['public']['Tables']['consultations']['Insert'];

export const useConsultations = (userId?: string) => {
  return useQuery({
    queryKey: ['consultations', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('consultations')
        .select(`
          *,
          mediators!inner(
            profiles!inner(full_name, email)
          )
        `)
        .or(`seeker_id.eq.${userId},mediator_id.eq.${userId}`)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
};

export const useCreateConsultation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (consultation: ConsultationInsert) => {
      const { data, error } = await supabase
        .from('consultations')
        .insert(consultation)
        .select()
        .single();
      
      if (error) throw error;

      // Send email notification to mediator (fire and forget)
      supabase.functions.invoke('send-new-consultation-email', {
        body: {
          consultationId: data.id,
        },
      }).catch((emailError) => {
        console.error('Failed to send consultation notification email:', emailError);
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
  });
};

export const useCreateFollowUpConsultation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceConsultation: {
      seeker_id: string;
      mediator_id: string;
      mediation_area: Database['public']['Enums']['mediation_area'];
      meeting_number: number | null;
      other_party_email?: string | null;
      other_party_name?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('consultations')
        .insert({
          seeker_id: sourceConsultation.seeker_id,
          mediator_id: sourceConsultation.mediator_id,
          mediation_area: sourceConsultation.mediation_area,
          meeting_number: (sourceConsultation.meeting_number || 1) + 1,
          other_party_email: sourceConsultation.other_party_email,
          other_party_name: sourceConsultation.other_party_name,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // Notify mediator about the new follow-up consultation
      supabase.functions.invoke('send-new-consultation-email', {
        body: { consultationId: data.id },
      }).catch(console.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      queryClient.invalidateQueries({ queryKey: ['mediatorConsultations'] });
    },
  });
};
